import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {EnvironmentService} from "@iw-shop/core/services/environment.service";
import {BehaviorSubject, lastValueFrom, Observable} from 'rxjs';
import {filter, map, take} from "rxjs/operators";
import {
    ApiDeliveryTour,
    ApiDeliveryTourDay, ApiDeliveryToursGetRequest,
    ApiDeliveryToursGetResponse
} from "@api/delivery-tours";
import {ChangeTimezone} from "@iw-shop/core/functions/date";
import {ApiDeliveryBlockedDays} from "@api/delivery-blocked-days";
import {Store} from "@ngrx/store";
import {AppState} from "@store/state/app.state";
import {DeliveryBlockedDaysService} from "@iw-shop/delivery-blocked-day/services/delivery-blocked-day.service";
import {DeliveryAreaService} from "@iw-shop/delivery-area/services/delivery-area.service";
import {DeliveryInformation} from "@iw-shop/delivery-tour/interfaces/delivery-information";
import {setShoppingCartDynamicArticles} from "@store/actions/shopping-cart.action";
import {HTTPQueue} from "@iw-shop/core/classes/http-queue";
import {shoppingCartTourDynamicArticleIdsTour} from "@store/actions/storage.action";
import {getSelectedCustomer} from "@store/selectors/user.selector";


@Injectable({
    providedIn: 'root'
})
export class DeliveryTourService {

    days: {
        value: number,
        name: 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday'
    }[] = [
        {
            value: 0,
            name: 'sunday'
        },
        {
            value: 1,
            name: 'monday'
        },
        {
            value: 2,
            name: 'tuesday'
        },
        {
            value: 3,
            name: 'wednesday'
        },
        {
            value: 4,
            name: 'thursday'
        },
        {
            value: 5,
            name: 'friday'
        },
        {
            value: 6,
            name: 'saturday'
        }
    ];


    protected deliveryArea?: { postcode: string; town: string };

    protected deliveryDateInMilliseconds: number = 0;
    protected orderDateInMilliseconds: number = 0;
    protected selectedDeliveryTours?: ApiDeliveryTour[];
    protected selectedDeliveryTour: BehaviorSubject<null | ApiDeliveryTour> = new BehaviorSubject<ApiDeliveryTour | null>(null);
    protected selectedDeliveryTourDay: ApiDeliveryTourDay | null = null;

    protected blockedDays: DeliveryInformation[] = [];
    protected deliveryPeriodCustomer: string = '';

    protected priceGroup: string | null = null;

    constructor(
        private http: HttpClient,
        private httpQueue: HTTPQueue,
        private store: Store<AppState>,
        private environmentService: EnvironmentService,
        private deliveryBlockedDayService: DeliveryBlockedDaysService,
        private deliveryAreaService: DeliveryAreaService
    ) {
    }


    get(params: ApiDeliveryToursGetRequest['params']): Observable<ApiDeliveryToursGetResponse> {
        const body: ApiDeliveryToursGetRequest = {
            id: 'deliveryTours',
            command: 'deliveryTours/get',
            params
        };
        return this.http.post<ApiDeliveryToursGetResponse>(this.environmentService.environment.urlApi, JSON.stringify(body));
    }

    async setToursByDB(deliveryArea?: { postcode: string, town: string }) {
        let filters: ApiDeliveryToursGetRequest['params']['filters'] = [
            {fields: ['default'], values: [true]}
        ];
        if (deliveryArea) {
            this.deliveryArea = deliveryArea;
            let deliveryAreaResponse = await lastValueFrom(
                this.httpQueue.invoke(
                    this.deliveryAreaService.get({
                        filters: [
                            {fields: ['postcode'], values: [deliveryArea.postcode]},
                            {fields: ['town'], values: [deliveryArea.town]},
                        ],
                        deliveryAreas: {}
                    })
                )
            );
            if (deliveryAreaResponse.result?.deliveryAreas.data[0]?.deliveryTourId) {
                filters = [{
                    fields: ['id'],
                    values: [deliveryAreaResponse.result?.deliveryAreas.data[0].deliveryTourId],
                    or: {
                        fields: ['default'],
                        values: [true]
                    }

                }]
            }
        }

        const blockedDaysResponse = await lastValueFrom(
            this.httpQueue.invoke(
                this.deliveryBlockedDayService.get()
            )
        );
        let blockedDays: ApiDeliveryBlockedDays[] = [];
        blockedDaysResponse?.result?.deliveryBlockedDays.data.map(day => {
            blockedDays.push(day)
        });
        this.setBlockedDays(blockedDays);

        let r = await lastValueFrom(
            this.httpQueue.invoke(
                this.get({
                        filters,
                        deliveryTours: {
                            sort: [{field: 'position', direction: 1}]
                        }
                    }
                )
            )
        );

        if (r.result?.deliveryTours.data &&
            (r.result?.deliveryTours.data.length ?? 0) > 0) {
            this.selectedDeliveryTours = r.result?.deliveryTours.data;
            this.chooseAndSetSelectedDeliveryTour(r.result?.deliveryTours.data);
        } else {
            this.selectedDeliveryTours = undefined;
            this.selectedDeliveryTour.next(null);
        }

    }

    getSelectedDeliveryTours(): ApiDeliveryTour[] | undefined {
        return this.selectedDeliveryTours;
    }

    getNextDeliveryTours(): { date: Date, periods: string[] }[] {
        let deliveryTours: { date: Date, periods: string[] }[] = [];

        for (let i = 0; i < 20; i++) {
            let date = new Date();
            date.setHours(23, 59, 59, 0);
            date.setDate(date.getDate() + i);
            this.setDeliveryDateInMilliseconds(date.getTime());
            this.setOrderDateInMilliseconds(date.getTime());
            this.setSelectedDeliveryTourDay();
            const selectedDeliveryTour = this.getSelectedDeliveryTour().value;
            if (selectedDeliveryTour &&
                this.isDeliveryTour(selectedDeliveryTour, {
                    day: date.getDay(),
                    deliveryDateInMilliseconds: date.getTime()
                }) &&
                !this.getBlockedDay(date)
            ) {
                deliveryTours.push({
                    date: date,
                    periods: this.getDeliveryPeriods()
                });
            }

        }

        return deliveryTours;
    }

    getDeliveryArea() {
        return this.deliveryArea;
    }

    setBlockedDays(blockedDays: DeliveryInformation[]) {
        this.blockedDays = blockedDays;
    }

    getBlockedDays(): DeliveryInformation[] {
        let blockedDays: DeliveryInformation[] = [];
        this.blockedDays.map(blockedDay => {
            if (
                (
                    (blockedDay.customerPriceGroups?.length ?? 0) == 0 ||
                    blockedDay.customerPriceGroups?.includes(this.priceGroup ?? '')
                )
                &&

                (
                    (blockedDay.deliveryTourIds?.length ?? 0) == 0 ||
                    blockedDay.deliveryTourIds.includes(this.getSelectedDeliveryTour().value?.id ?? '')
                )
                &&
                (blockedDay.deliveryPeriods?.length ?? 0) == 0
            ) {
                blockedDays.push(blockedDay)
            }
        })
        return blockedDays;
    }

    public getBlockedDay(date: Date): null | DeliveryInformation {
        let deliveryInformations: DeliveryInformation[] = this.getBlockedDays();
        let selectedDeliveryInformation = null;
        let dateFrom;
        let dateTo;
        deliveryInformations.map(di => {
            dateFrom = new Date(di.from);
            dateTo = new Date(di.to);
            if (
                (
                    date >= dateFrom &&
                    date <= dateTo
                )
            ) {
                selectedDeliveryInformation = di;
            }

        })
        return selectedDeliveryInformation;
    }

    setDeliveryDateInMilliseconds(milliseconds: number) {
        this.deliveryDateInMilliseconds = milliseconds;
    }

    setOrderDateInMilliseconds(milliseconds: number) {
        this.orderDateInMilliseconds = milliseconds;
    }

    chooseAndSetSelectedDeliveryTour(deliveryTours: ApiDeliveryTour[]): void {
        let length = deliveryTours.length;
        let dynamicArticleIdsInShoppingCart: string[] = [], lastTourId = this.getSelectedDeliveryTour().value?.id;
        console.log('deliveryTour.chooseAndSetSelectedDeliveryTour deliveryTours', deliveryTours);
        this.store.select(state => state.shoppingCart?.items?.dynamicArticles)
            .pipe(take(1))
            .subscribe(
                r => {
                    if (r) {
                        r.map(da => {
                            dynamicArticleIdsInShoppingCart.push(da.id);
                        });
                    }
                }
            );
        let isTourSelected = false, tourDynamicArticleIds: string[] | undefined = [];
        for (let i = 0; i < length; i++) {
            let tour = deliveryTours[i], goToNextTour: boolean = false;
            if (tour.requiredPriceGroups &&
                tour.requiredPriceGroups.length > 0 &&
                !tour.requiredPriceGroups.includes(this.priceGroup ?? '')) {
                goToNextTour = true;
            }
            if (tour.excludedDynamicArticleIds) {
                tour.excludedDynamicArticleIds.forEach(id => {
                    if (dynamicArticleIdsInShoppingCart.includes(id)) {
                        goToNextTour = true;
                    }
                })
            }
            if (tour.requiredDynamicArticleIds) {
                tour.requiredDynamicArticleIds.forEach(id => {
                    if (!dynamicArticleIdsInShoppingCart.includes(id)) {
                        goToNextTour = true;
                    }
                })
            }
            if (goToNextTour) {
                continue;
            }
            console.log('deliveryTour.chooseAndSetSelectedDeliveryTour select tour', tour);
            this.selectedDeliveryTour.next(tour);
            isTourSelected = true;
            tourDynamicArticleIds = tour.dynamicArticleIds;
            break;
        }
        if (!isTourSelected) {
            console.log('deliveryTour.chooseAndSetSelectedDeliveryTour select tour', null);
            this.selectedDeliveryTour.next(null);
        }
        // Es wurde eine andere Tour ausgewählt > Dynamische Artikel erneut setzen
        if(lastTourId != this.getSelectedDeliveryTour().value?.id){
            this.setDynamicArticleIdsForTour(tourDynamicArticleIds);
        }
    }

    setDynamicArticleIdsForTour(dynamicArticleIds?: string[]) {
        let dynamicArticles: { amount: number, id: string }[] = [], dynamicArticleFromLastSelectedTour: string[] = [];

        // Automatisch hinzugefügte Artikel der letzten Tour löschen
        this.store.select(state => state.storage).pipe(
            take(1)
        ).subscribe(
                r => {
                    r.shoppingCart?.dynamicArticleIds?.tour?.map(
                        id => {
                            dynamicArticles.push({amount: 0, id});
                            dynamicArticleFromLastSelectedTour.push(id);
                        }
                    )
                }
        );

        // Artikel von der jetzigen Tour hinzufügen
        dynamicArticleIds?.map(id => {
            dynamicArticles.push({ amount: 1, id });
        })

        const setFromLastTour = new Set(dynamicArticleFromLastSelectedTour);
        const hasSameElements = dynamicArticleIds?.every(id => setFromLastTour.has(id)) && dynamicArticleFromLastSelectedTour.every(id => dynamicArticleIds?.includes(id));

        // Daten zwischenspeichern
        if(!hasSameElements){
            this.store.dispatch(shoppingCartTourDynamicArticleIdsTour({ids: dynamicArticleIds ?? []}));
        }

        if(dynamicArticles.length > 0){
            console.log('setDynamicArticleIdsForTour', this.selectedDeliveryTour.value?.name, dynamicArticles);
            this.store.dispatch(setShoppingCartDynamicArticles({
                jsonId: 'deliveryTour.setDynamicArticleIdsForTour',
                dynamicArticles
            }));
        }

    }

    resetDeliveryTourDay() {
        this.selectedDeliveryTourDay = null;
    }

    setSelectedDeliveryTourDay(): void {
        let date = new Date(this.deliveryDateInMilliseconds ?? 0);
        const selectedDeliveryTour = this.getSelectedDeliveryTour().value;
        this.days.map(day => {
            if (day.value == date.getDay() &&
                selectedDeliveryTour?.days[day.name].available) {
                this.selectedDeliveryTourDay = selectedDeliveryTour.days[day.name];
            }
        });
    }

    getSelectedDeliveryTour(): BehaviorSubject<ApiDeliveryTour | null> {
        return this.selectedDeliveryTour;
    }

    getShoppingCartMerchandiseValue(priceMode: 'net' | 'gross', excludedGroups?: string[]): number {
        let cartValueWithExcludedGroups = 0, isExcludedGroup: boolean;
        this.store.select(state => state.shoppingCart)
            .pipe(take(1))
            .subscribe(
                sc => {
                    if (sc.items) {
                        sc.items.articles.map(
                            a => {
                                isExcludedGroup = false;
                                // check is excluded
                                a.groups.map(g => {
                                    if (excludedGroups?.includes(g.groupName) ||
                                        excludedGroups?.includes(g.group1Name)) {
                                        isExcludedGroup = true;
                                    }
                                });
                                if (!isExcludedGroup) {
                                    if (priceMode == 'net') {
                                        cartValueWithExcludedGroups = a.netSumPrice + cartValueWithExcludedGroups;
                                    } else {
                                        cartValueWithExcludedGroups = a.grossSumPrice + cartValueWithExcludedGroups;
                                    }
                                }
                            }
                        )
                    }
                }
            );
        return cartValueWithExcludedGroups;
    }

    getShoppingCartArticles(excludedGroups?: string[], unitTypes?: string[]): number {
        let articles = 0, isExcluded: boolean;
        this.store.select(state => state.shoppingCart)
            .pipe(take(1))
            .subscribe(
                sc => {
                    if (sc.items) {
                        sc.items.articles.map(
                            a => {
                                isExcluded = false;
                                // check is excluded
                                a.groups.map(g => {
                                    if (excludedGroups?.includes(g.groupName) ||
                                        excludedGroups?.includes(g.group1Name)) {
                                        isExcluded = true;
                                    }
                                });

                                if (unitTypes &&
                                    (unitTypes?.length ?? 0) > 0) {
                                    if (!unitTypes.includes(a.unitType)) {
                                        isExcluded = true;
                                    }
                                }

                                if (!isExcluded) {
                                    articles = articles + a.amount;
                                }
                            }
                        )
                    }
                }
            );
        return articles;
    }

    isCustomerDefaultDeliveryPeriod(): boolean {
        return !!(this.selectedDeliveryTourDay?.deliveryPeriodCustomer?.available &&
            !this.isDateDeadline({
                deliveryDateInMilliseconds: this.deliveryDateInMilliseconds ?? 0,
                latestOrderTimeInDays: this.selectedDeliveryTourDay?.deliveryPeriodCustomer.latestOrderTimeInDays ?? 0,
                latestOrderTimeInHours: this.selectedDeliveryTourDay?.deliveryPeriodCustomer.latestOrderTimeInHours ?? 0,
                latestOrderTimeInMinutes: this.selectedDeliveryTourDay?.deliveryPeriodCustomer.latestOrderTimeInMinutes ?? 0
            }));

    }

    setPriceGroup(priceGroup: string | null): void {
        this.priceGroup = priceGroup;
    }

    getDeliveryNotes(params?: { deliveryPeriod?: string }): string[] | null {
        let deliveryNotes: string[] = [];

        /*    let deliveryDate = new Date(this.deliveryDateDateInMilliseconds);
            let deadlineDate = new Date(this.orderDateInMilliseconds);*/

        // Bei dem Tag ist eine Note hinterlegt, welche noch nicht das DeadlineDate erreicht hat
        if (
            this.selectedDeliveryTourDay?.note &&
            this.selectedDeliveryTourDay?.note.length > 0) {
            // Falls ein Einblenddatum gesetzt ist > prüfen, alternativ auslesen, wenn es nicht gesetzt ist.
            if (
                (
                    this.selectedDeliveryTourDay?.noteVisibilityBeforeDeliveryDateInDays
                    &&
                    this.isDateDeadline({
                        deliveryDateInMilliseconds: this.deliveryDateInMilliseconds ?? 0,
                        latestOrderTimeInDays: this.selectedDeliveryTourDay?.noteVisibilityBeforeDeliveryDateInDays ?? 0,
                        latestOrderTimeInHours: this.selectedDeliveryTourDay?.noteVisibilityBeforeDeliveryDateInHours ?? 0,
                        latestOrderTimeInMinutes: this.selectedDeliveryTourDay?.noteVisibilityBeforeDeliveryDateInMinutes ?? 0
                    })
                )
                ||
                !this.selectedDeliveryTourDay?.noteVisibilityBeforeDeliveryDateInDays
            ) {
                deliveryNotes.push(this.selectedDeliveryTourDay?.note);
            }
        }

        // Notes von dem Lieferzeitraum des Kunden
        if (this.isCustomerDefaultDeliveryPeriod() &&
            this.deliveryPeriodCustomer == params?.deliveryPeriod &&
            this.selectedDeliveryTourDay?.deliveryPeriodCustomer?.note &&
            this.selectedDeliveryTourDay?.deliveryPeriodCustomer.note.length > 0) {
            deliveryNotes.push(this.selectedDeliveryTourDay?.deliveryPeriodCustomer.note);
        }

        // Notes von den Lieferzeiträumen
        this.selectedDeliveryTourDay?.deliveryPeriods?.map(
            dp => {
                if (
                    dp.deliveryPeriod == params?.deliveryPeriod &&
                    dp.note &&
                    dp.note.length > 0) {
                    deliveryNotes.push(dp.note);
                }
            }
        )


        if (deliveryNotes.length == 0) {
            return null;
        }
        return deliveryNotes;
    }

    getDeliveryPeriods(): string[] {

        let deliveryPeriods: string[] = [];

        /*let deliveryTour = this.getSelectedDeliveryTour().value;
        console.log(new Date(this.deliveryDateInMilliseconds));
        console.log(this.selectedDeliveryTourDay?.deliveryPeriods);*/

        // Lieferzeitraum für Kunden hinzufügen
        if(this.selectedDeliveryTourDay?.deliveryPeriodCustomer?.available){
            this.store.select(getSelectedCustomer).pipe(
                take(1)
            ).subscribe(
                c => {
                    if( (c?.defaultDeliveryPeriod?.length ?? 0) > 0){
                        deliveryPeriods.push(c?.defaultDeliveryPeriod ?? '');
                    }
                }
            )
        }

        this.selectedDeliveryTourDay?.deliveryPeriods?.map(
            dp => {
                if (
                    !this.isDateDeadline({
                        debug: true,
                        deliveryDateInMilliseconds: this.deliveryDateInMilliseconds ?? 0,
                        latestOrderTimeInDays: dp.latestOrderTimeInDays ?? 0,
                        latestOrderTimeInHours: dp.latestOrderTimeInHours ?? 0,
                        latestOrderTimeInMinutes: dp.latestOrderTimeInMinutes ?? 0
                    })) {
                    deliveryPeriods.push(dp.deliveryPeriod);
                }
            }
        );

        // Prüfen, ob Lieferzeiträume für den Tag gesperrt sind
        let dateFromException;
        let dateToException;
        this.blockedDays?.map(e => {
            dateFromException = new Date(e.from);
            dateToException = new Date(e.to);

            let date = new Date(this.deliveryDateInMilliseconds);
            if (
                (
                    date >= dateFromException &&
                    date <= dateToException
                )
                &&
                (
                    (e.customerPriceGroups?.length ?? 0) == 0 ||
                    e.customerPriceGroups?.includes(this.priceGroup ?? '')
                )
                &&
                (
                    (e.deliveryTourIds?.length ?? 0) == 0 ||
                    e.deliveryTourIds.includes(this.getSelectedDeliveryTour()?.value?.id ?? '')

                )
            ) {
                e.deliveryPeriods?.map(
                    excludeDP => {
                        if (deliveryPeriods?.includes(excludeDP)) {
                            for (let i = 0; i < deliveryPeriods.length; i++) {
                                if (deliveryPeriods[i] == excludeDP) {
                                    deliveryPeriods.splice(i, 1)
                                }
                            }
                        }
                    }
                );
            }
        });
        return deliveryPeriods;
    }

    isDateDeadline(params: {
        deliveryDateInMilliseconds: number,
        latestOrderTimeInDays: number,
        latestOrderTimeInHours: number,
        latestOrderTimeInMinutes: number,
        debug?: boolean
    }): boolean {
        let orderDate = ChangeTimezone(new Date(), 'Europe/Berlin');

        let latestOrderInMinutes = (params.latestOrderTimeInHours * 60) + params.latestOrderTimeInMinutes;
        let latestOrderInDays = params.latestOrderTimeInDays + (latestOrderInMinutes / 60 / 24);
        let latestOrderDate = ChangeTimezone(new Date(params.deliveryDateInMilliseconds - (latestOrderInDays * 24 * 60 * 60 * 1000)), 'Europe/Berlin');

        // Deadline abgelaufen
        if (orderDate.getTime() >= latestOrderDate.getTime()) {
            return true;
        }
        return false;
    }

    isDeliveryTour(ApiDeliveryTour: ApiDeliveryTour, options: {
        deliveryDateInMilliseconds: number,
        day: number
    }): boolean {


        let isDeliveryTour: boolean = false;

        let tourDay: ApiDeliveryTourDay;
        // 0 = Sonntag
        this.days.map(day => {
            if (day.value != options.day) {
                return;
            }
            tourDay = ApiDeliveryTour.days[day.name];

            if (!tourDay.available) {
                return;
            }


            if (tourDay.dateForSecondWeekInterval) {
                let date1 = new Date(tourDay.dateForSecondWeekInterval);
                let date2 = new Date(options.deliveryDateInMilliseconds);
                let difference = date2.getTime() - date1.getTime();
                let weeks = Math.ceil(difference / (1000 * 3600 * 24 * 7));
                if (weeks % 2 == 0) {
                    return;
                }
            }
            if (this.isDateDeadline({
                deliveryDateInMilliseconds: options.deliveryDateInMilliseconds,
                latestOrderTimeInDays: tourDay.latestOrderTimeInDays ?? 0,
                latestOrderTimeInHours: tourDay.latestOrderTimeInHours ?? 0,
                latestOrderTimeInMinutes: tourDay.latestOrderTimeInMinutes ?? 0,
                debug: false
            })) {
                return;
            }
            if (tourDay.available) {
                isDeliveryTour = true;
            }
        });

        return isDeliveryTour;
    }

}
